import '../styles/SettingsConfigPanel.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useProjectCollection, projectData } from '../lib/projectData';

const COLLECTION = 'application_settings';
const SETTINGS_ID = 'settings-local-default';

const SHIPPED_DEFAULTS = {
  defaultInputFormat: 'wordlist',
  defaultThreadCount: 8,
  defaultPolicy: 'standard',
  storageLocation: 'C:\\Users\\operator\\AppData\\Local\\Brute',
  loggingLevel: 'verbose',
  logToFile: true,
};

const INPUT_FORMATS = [
  { value: 'wordlist', label: 'Wordlist' },
  { value: 'masked input', label: 'Masked input' },
  { value: 'custom list', label: 'Custom list' },
];
const POLICIES = [
  { value: 'standard', label: 'Standard' },
  { value: 'strict', label: 'Strict' },
  { value: 'lenient', label: 'Lenient' },
];
const LOG_LEVELS = [
  { value: 'verbose', label: 'Verbose' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
];

const FIELD_LABELS = {
  defaultInputFormat: 'Default input format',
  defaultThreadCount: 'Default thread count',
  defaultPolicy: 'Default policy',
  storageLocation: 'Storage location',
  loggingLevel: 'Logging level',
  logToFile: 'Log to file',
};

const toDraft = (record) => ({
  defaultInputFormat: record.defaultInputFormat ?? SHIPPED_DEFAULTS.defaultInputFormat,
  defaultThreadCount: String(record.defaultThreadCount ?? SHIPPED_DEFAULTS.defaultThreadCount),
  defaultPolicy: record.defaultPolicy ?? SHIPPED_DEFAULTS.defaultPolicy,
  storageLocation: record.storageLocation ?? SHIPPED_DEFAULTS.storageLocation,
  loggingLevel: record.loggingLevel ?? SHIPPED_DEFAULTS.loggingLevel,
  logToFile: record.logToFile !== false,
});

function validate(draft) {
  const errors = {};
  if (!draft.defaultInputFormat) errors.defaultInputFormat = 'Choose a default input format.';
  const threads = Number(draft.defaultThreadCount);
  if (draft.defaultThreadCount.trim() === '' || !Number.isFinite(threads)) {
    errors.defaultThreadCount = 'Enter a whole number of threads.';
  } else if (!Number.isInteger(threads) || threads < 1 || threads > 256) {
    errors.defaultThreadCount = 'Use a whole number between 1 and 256.';
  }
  if (!draft.defaultPolicy) errors.defaultPolicy = 'Choose a default policy.';
  if (!draft.storageLocation.trim()) {
    errors.storageLocation = 'Enter the local folder where Brute keeps settings, history and logs.';
  }
  if (!draft.loggingLevel) errors.loggingLevel = 'Choose a logging level.';
  return errors;
}

function formatTimestamp(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toISOString().slice(0, 10)} ${date.toISOString().slice(11, 19)} UTC`;
}

function Skeleton() {
  return (
    <div className="scp-skeleton" aria-hidden="true">
      {[0, 1, 2, 3].map((row) => (
        <div className="scp-skel-row" key={`skel-${row}`}>
          <span className="scp-skel scp-skel--label" />
          <span className="scp-skel scp-skel--input" />
        </div>
      ))}
    </div>
  );
}

function SettingsConfigPanel() {
  const { data, loading, error, refresh } = useProjectCollection(COLLECTION);

  // Read the single local settings record, preferring the canonical id.
  const record = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;
    return data.find((item) => item.id === SETTINGS_ID) || data[0];
  }, [data]);

  const [draft, setDraft] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | success | error
  const [savedAt, setSavedAt] = useState(null);
  const [writeError, setWriteError] = useState(null);
  const [emptyFirstUse, setEmptyFirstUse] = useState(false);
  const [resetNotice, setResetNotice] = useState(false);

  const fieldRefs = useRef({});
  const hydratedId = useRef(null);

  useEffect(() => {
    if (loading || error) return;
    if (!record) {
      setDraft(toDraft(SHIPPED_DEFAULTS));
      setEmptyFirstUse(true);
      hydratedId.current = 'first-use';
      return;
    }
    if (hydratedId.current === record.id) return;
    hydratedId.current = record.id;
    setDraft(toDraft(record));
    setEmptyFirstUse(false);
    setFieldErrors({});
    setShowSummary(false);
  }, [record, loading, error]);

  const update = (key, value) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    if (saveStatus !== 'idle') {
      setSaveStatus('idle');
      setWriteError(null);
    }
    setResetNotice(false);
  };

  const registerField = (key) => (node) => {
    fieldRefs.current[key] = node;
  };

  const focusField = (key) => {
    const node = fieldRefs.current[key];
    if (node && node.focus) node.focus();
  };

  const persist = async (values) => {
    setSaving(true);
    setShowSummary(false);
    setSaveStatus('idle');
    setWriteError(null);
    setResetNotice(false);
    const payload = { ...values, defaultThreadCount: Number(values.defaultThreadCount) };
    try {
      const existing = record && record.id ? record.id : SETTINGS_ID;
      const result = record
        ? await projectData.update(COLLECTION, existing, payload)
        : await projectData.create(COLLECTION, { id: SETTINGS_ID, ...payload });
      const stamp = (result && (result.updatedAt || result.createdAt)) || record?.updatedAt || null;
      setSavedAt(stamp);
      setSaveStatus('success');
      setEmptyFirstUse(false);
    } catch (err) {
      setSaveStatus('error');
      setWriteError((err && err.message) || 'The local store rejected the write.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!draft) return;
    const errors = validate(draft);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setShowSummary(true);
      setSaveStatus('idle');
      setWriteError(null);
      return;
    }
    persist(draft);
  };

  const handleRetry = () => {
    if (!draft) return;
    const errors = validate(draft);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setShowSummary(true);
      return;
    }
    persist(draft);
  };

  const handleReset = () => {
    setDraft(toDraft(SHIPPED_DEFAULTS));
    setFieldErrors({});
    setShowSummary(false);
    setSaveStatus('idle');
    setWriteError(null);
    setResetNotice(true);
  };

  const readError = Boolean(error);
  const showForm = !loading && !readError && Boolean(draft);
  const errorEntries = Object.keys(fieldErrors);
  const hasErrors = errorEntries.length > 0;

  const fieldClass = (key) => (fieldErrors[key] ? 'scp-field scp-field--invalid' : 'scp-field');

  const errorText = (key) =>
    fieldErrors[key] ? (
      <span className="scp-field__error" role="alert">{fieldErrors[key]}</span>
    ) : null;

  return (
    <section className="scp-root" aria-labelledby="scp-title">
      <div className="scp-rail">
        <div className="scp-panel">
          <header className="scp-panel__head">
            <div>
              <p className="scp-panel__kicker">Local configuration</p>
              <h2 className="scp-panel__title" id="scp-title">Run &amp; logging settings</h2>
              <p className="scp-panel__lead">
                Defaults applied to every new test run, where Brute keeps its local files, and how much it logs.
                Everything stays on this machine.
              </p>
            </div>
            <span className="scp-sticker">
              <span className="scp-sticker__dot" aria-hidden="true" />
              Authorized targets only
            </span>
          </header>

          {/* Read state */}
          {loading && (
            <div aria-busy="true" aria-live="polite" aria-label="Reading current settings from local storage">
              <p className="scp-panel__kicker" style={{ marginBottom: '14px' }}>Reading current settings…</p>
              <Skeleton />
            </div>
          )}

          {readError && (
            <div className="scp-status scp-status--error scp-read-error" role="alert">
              <div className="scp-status__body">
                <span className="scp-status__label">Settings could not be read</span>
                <span className="scp-status__text">
                  {String(error && (error.message || error)) || 'The local store did not respond.'} Your saved
                  configuration is untouched.
                </span>
              </div>
              <button type="button" className="scp-btn scp-btn--ghost" onClick={() => refresh && refresh()}>
                Retry read
              </button>
            </div>
          )}

          {showForm && (
            <>
              {emptyFirstUse && (
                <div className="scp-notice" role="status">
                  <span className="scp-notice__tag">First use</span>
                  <span>
                    No saved configuration yet — the shipped defaults are shown below. Adjust them and save to
                    create your local settings.
                  </span>
                </div>
              )}

              {resetNotice && !showSummary && saveStatus === 'idle' && (
                <div className="scp-notice" role="status">
                  <span className="scp-notice__tag">Draft reset</span>
                  <span>
                    The shipped defaults are back in the form. They apply after you save the configuration.
                  </span>
                </div>
              )}

              {showSummary && hasErrors && (
                <div className="scp-summary" role="alert" aria-labelledby="scp-summary-title">
                  <div className="scp-summary__head">
                    <h3 className="scp-summary__title" id="scp-summary-title">Fix before saving</h3>
                    <span className="scp-summary__count">
                      {errorEntries.length} {errorEntries.length === 1 ? 'field needs' : 'fields need'} attention
                    </span>
                  </div>
                  <ul className="scp-summary__list">
                    {errorEntries.map((key) => (
                      <li key={key}>
                        <button
                          type="button"
                          className="scp-summary__link"
                          onClick={() => focusField(key)}
                        >
                          <span className="scp-summary__field">{FIELD_LABELS[key]}</span>
                          {fieldErrors[key]}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {saveStatus === 'error' && writeError && (
                <div className="scp-status scp-status--error" role="alert">
                  <div className="scp-status__body">
                    <span className="scp-status__label">Save failed</span>
                    <span className="scp-status__text">
                      {writeError} Your entries are kept — fix the cause and retry.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="scp-btn scp-btn--ghost"
                    onClick={handleRetry}
                    aria-busy={saving ? 'true' : 'false'}
                    disabled={saving}
                  >
                    {saving ? 'Retrying…' : 'Retry save'}
                  </button>
                </div>
              )}

              {saveStatus === 'success' && (
                <div className="scp-status scp-status--ok" role="status">
                  <div className="scp-status__body">
                    <span className="scp-status__label">Configuration saved</span>
                    <span className="scp-status__text">
                      {savedAt
                        ? 'Run defaults and logging preferences written to the local store.'
                        : 'Run defaults and logging preferences written. The store did not return an update time.'}
                    </span>
                    {savedAt && (
                      <span className="scp-status__meta">Updated {formatTimestamp(savedAt)}</span>
                    )}
                  </div>
                </div>
              )}

              <form className="scp-form" onSubmit={handleSubmit} noValidate>
                <div className="scp-grid">
                  <fieldset className="scp-group">
                    <legend className="scp-group__title">Run defaults</legend>
                    <span className="scp-group__note">prefilled on New Test Run</span>

                    <div className={fieldClass('defaultInputFormat')}>
                      <label className="scp-field__label" htmlFor="scp-inputFormat">Default input format</label>
                      <select
                        id="scp-inputFormat"
                        className="scp-select"
                        ref={registerField('defaultInputFormat')}
                        value={draft.defaultInputFormat}
                        aria-invalid={fieldErrors.defaultInputFormat ? 'true' : 'false'}
                        onChange={(e) => update('defaultInputFormat', e.target.value)}
                      >
                        {INPUT_FORMATS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      {errorText('defaultInputFormat')}
                    </div>

                    <div className={fieldClass('defaultThreadCount')}>
                      <label className="scp-field__label" htmlFor="scp-threads">Default thread count</label>
                      <input
                        id="scp-threads"
                        className="scp-input scp-input--mono"
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max="256"
                        step="1"
                        ref={registerField('defaultThreadCount')}
                        value={draft.defaultThreadCount}
                        aria-invalid={fieldErrors.defaultThreadCount ? 'true' : 'false'}
                        aria-describedby="scp-threads-help"
                        onChange={(e) => update('defaultThreadCount', e.target.value)}
                      />
                      <span className="scp-field__help" id="scp-threads-help">Whole number, 1–256.</span>
                      {errorText('defaultThreadCount')}
                    </div>

                    <div className={fieldClass('defaultPolicy')}>
                      <label className="scp-field__label" htmlFor="scp-policy">Default policy</label>
                      <select
                        id="scp-policy"
                        className="scp-select"
                        ref={registerField('defaultPolicy')}
                        value={draft.defaultPolicy}
                        aria-invalid={fieldErrors.defaultPolicy ? 'true' : 'false'}
                        onChange={(e) => update('defaultPolicy', e.target.value)}
                      >
                        {POLICIES.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      {errorText('defaultPolicy')}
                    </div>
                  </fieldset>

                  <fieldset className="scp-group">
                    <legend className="scp-group__title">Logging preferences</legend>
                    <span className="scp-group__note">applies to every run</span>

                    <div className={fieldClass('loggingLevel')}>
                      <label className="scp-field__label" htmlFor="scp-logLevel">Logging level</label>
                      <select
                        id="scp-logLevel"
                        className="scp-select"
                        ref={registerField('loggingLevel')}
                        value={draft.loggingLevel}
                        aria-invalid={fieldErrors.loggingLevel ? 'true' : 'false'}
                        onChange={(e) => update('loggingLevel', e.target.value)}
                      >
                        {LOG_LEVELS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      {errorText('loggingLevel')}
                    </div>

                    <div className="scp-toggle-row">
                      <span className="scp-toggle-row__text">
                        <span className="scp-field__label">Log to file</span>
                        <span className="scp-toggle-row__state" id="scp-logfile-state">{draft.logToFile ? 'On — writes log files' : 'Off — console only'}</span>
                      </span>
                      <button
                        type="button"
                        className="scp-toggle"
                        role="switch"
                        aria-checked={draft.logToFile}
                        aria-labelledby="scp-logfile-state"
                        onClick={() => update('logToFile', !draft.logToFile)}
                      >
                        <span className="scp-toggle__knob" aria-hidden="true" />
                      </button>
                    </div>
                  </fieldset>

                  <div className={`scp-group scp-storage ${fieldErrors.storageLocation ? 'scp-field--invalid' : ''}`}>
                    <label className="scp-field__label" htmlFor="scp-storagePath">Storage location</label>
                    <input
                      id="scp-storagePath"
                      className="scp-input scp-input--mono"
                      type="text"
                      spellCheck="false"
                      autoComplete="off"
                      ref={registerField('storageLocation')}
                      value={draft.storageLocation}
                      aria-invalid={fieldErrors.storageLocation ? 'true' : 'false'}
                      aria-describedby="scp-storage-help"
                      onChange={(e) => update('storageLocation', e.target.value)}
                    />
                    <span className="scp-field__help" id="scp-storage-help">
                      Local folder for settings, run history and log files on this machine.
                    </span>
                    {errorText('storageLocation')}
                  </div>
                </div>

                <div className="scp-actions">
                  <button
                    type="submit"
                    className="scp-btn scp-btn--primary"
                    aria-busy={saving ? 'true' : 'false'}
                    disabled={saving}
                  >
                    {saving && <span className="scp-btn__spinner" aria-hidden="true" />}
                    {saving ? 'Saving…' : 'Save configuration'}
                  </button>
                  <button type="button" className="scp-btn scp-btn--ghost" onClick={handleReset} disabled={saving}>
                    Reset to defaults
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default SettingsConfigPanel;